import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProcessingParameters,
  ProcessingMode,
  ValidationErrors,
  VALIDATION_RULES
} from '../types/firstProcessing.types';

interface UseProcessingParametersReturn {
  parameters: ProcessingParameters;
  validationErrors: ValidationErrors;
  setTss: (value: string) => void;
  setOffset: (value: string) => void;
  setMode: (value: ProcessingMode) => void;
  setIntrplMax: (value: string) => void;
  validateParameters: () => boolean;
  resetParameters: () => void;
  loadFromLocalStorage: () => void;
  saveToLocalStorage: () => void;
  anchorTime: string;
  setAnchorTime: (value: string) => void;
  isConst: boolean;
}

const STORAGE_KEY = 'firstProcessingParameters';

const initialParameters: ProcessingParameters = {
  tss: '',
  offset: '',
  mode: 'intrpl' as ProcessingMode,
  intrplMax: '60'
};

const toAnchorStr = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};

export const useProcessingParameters = (): UseProcessingParametersReturn => {
  const { t } = useTranslation('dataProcessing');
  const [parameters, setParameters] = useState<ProcessingParameters>(initialParameters);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [anchorTime, setAnchorTime] = useState<string>('');

  const loadFromLocalStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setParameters({
          ...initialParameters,
          ...parsed
        });
      }
    } catch (error) {
      console.error('Failed to load parameters from localStorage:', error);
    }
  }, []);

  const saveToLocalStorage = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parameters));
    } catch (error) {
      console.error('Failed to save parameters to localStorage:', error);
    }
  }, [parameters]);

  // Load parameters from localStorage on mount
  useEffect(() => {
    loadFromLocalStorage();
  }, [loadFromLocalStorage]);

  // Auto-save parameters to localStorage when they change
  useEffect(() => {
    if (parameters.tss || parameters.offset || parameters.mode) {
      saveToLocalStorage();
    }
  }, [parameters, saveToLocalStorage]);

  const validateTss = useCallback((value: string): string | undefined => {
    if (!value || value.trim() === '') {
      return t('validation.required', { field: t('inputParameters.tss') });
    }

    if (!VALIDATION_RULES.tss.pattern?.test(value)) {
      return t('validation.positiveDecimalError', { field: t('inputParameters.tss'), min: VALIDATION_RULES.tss.min, max: VALIDATION_RULES.tss.max });
    }

    const num = parseFloat(value);
    if (num < VALIDATION_RULES.tss.min || num > VALIDATION_RULES.tss.max) {
      return t('validation.positiveDecimalError', { field: t('inputParameters.tss'), min: VALIDATION_RULES.tss.min, max: VALIDATION_RULES.tss.max });
    }

    return undefined;
  }, [t]);

  const validateOffset = useCallback((value: string): string | undefined => {
    if (!value || value.trim() === '') {
      return t('validation.required', { field: t('inputParameters.offset') });
    }

    if (!VALIDATION_RULES.offset.pattern?.test(value)) {
      return t('validation.decimalRangeError', { field: t('inputParameters.offset'), min: VALIDATION_RULES.offset.min, max: VALIDATION_RULES.offset.max });
    }

    const num = parseFloat(value);
    if (num < VALIDATION_RULES.offset.min || num > VALIDATION_RULES.offset.max) {
      return t('validation.decimalRangeError', { field: t('inputParameters.offset'), min: VALIDATION_RULES.offset.min, max: VALIDATION_RULES.offset.max });
    }

    return undefined;
  }, [t]);

  const validateIntrplMax = useCallback((value: string, mode: ProcessingMode): string | undefined => {
    if (mode !== 'intrpl') {
      return undefined; // Not required for other modes
    }

    if (!value || value.trim() === '') {
      return t('validation.required', { field: 'Interpolation max' });
    }

    if (!VALIDATION_RULES.intrplMax.pattern?.test(value)) {
      return t('validation.positiveDecimalError', { field: 'Interpolation max', min: VALIDATION_RULES.intrplMax.min, max: VALIDATION_RULES.intrplMax.max });
    }

    const num = parseFloat(value);
    if (num < VALIDATION_RULES.intrplMax.min || num > VALIDATION_RULES.intrplMax.max) {
      return t('validation.positiveDecimalError', { field: 'Interpolation max', min: VALIDATION_RULES.intrplMax.min, max: VALIDATION_RULES.intrplMax.max });
    }

    return undefined;
  }, [t]);

  const setTss = useCallback((value: string) => {
    setParameters(prev => ({ ...prev, tss: value }));
    const error = validateTss(value);
    setValidationErrors(prev => ({ ...prev, tss: error }));
  }, [validateTss]);

  const setOffset = useCallback((value: string) => {
    setParameters(prev => ({ ...prev, offset: value }));
    const error = validateOffset(value);
    setValidationErrors(prev => ({ ...prev, offset: error }));
  }, [validateOffset]);


  const setMode = useCallback((value: ProcessingMode) => {
    setParameters(prev => ({ ...prev, mode: value }));
    setValidationErrors(prev => ({ ...prev, mode: undefined }));
    
    // Revalidate intrplMax when mode changes
    if (value === 'intrpl') {
      const intrplError = validateIntrplMax(parameters.intrplMax, value);
      setValidationErrors(prev => ({ ...prev, intrplMax: intrplError }));
    } else {
      setValidationErrors(prev => ({ ...prev, intrplMax: undefined }));
    }
  }, [parameters.intrplMax, validateIntrplMax]);

  const setIntrplMax = useCallback((value: string) => {
    setParameters(prev => ({ ...prev, intrplMax: value }));
    const error = validateIntrplMax(value, parameters.mode);
    setValidationErrors(prev => ({ ...prev, intrplMax: error }));
  }, [parameters.mode, validateIntrplMax]);

  const validateParameters = useCallback((): boolean => {
    const errors: ValidationErrors = {};

    const tssError = validateTss(parameters.tss);
    if (tssError) errors.tss = tssError;

    const offsetError = validateOffset(parameters.offset);
    if (offsetError) errors.offset = offsetError;

    const intrplMaxError = validateIntrplMax(parameters.intrplMax, parameters.mode);
    if (intrplMaxError) errors.intrplMax = intrplMaxError;

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [parameters, validateTss, validateOffset, validateIntrplMax]);

  const resetParameters = useCallback(() => {
    setParameters(initialParameters);
    setValidationErrors({});
    setAnchorTime('');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const tssNum = parseFloat(parameters.tss);
  // Default to const (offset input) until TSS is a valid positive number.
  // Switch to var (anchor-time input) only when TSS is valid AND not an exact divisor of 60.
  const isConst = !Number.isFinite(tssNum) || tssNum <= 0
    || Math.abs((60 / tssNum) - Math.round(60 / tssNum)) < 1e-9;

  return {
    parameters,
    validationErrors,
    setTss,
    setOffset,
    setMode,
    setIntrplMax,
    validateParameters,
    resetParameters,
    loadFromLocalStorage,
    saveToLocalStorage,
    anchorTime,
    setAnchorTime,
    isConst
  };
};

export { toAnchorStr };